import { Component, OnInit, Optional, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../core/services/toast.service';
import { OrganizationService } from '../../../../core/services/organization/organization.service';
import { TimezoneService } from '../../../../core/services/organization/timezone.service';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import {
  CountryISO,
  SearchCountryField,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PhoneInputDirective } from '../../../../shared/directives/phone-input.directive';
import { SelectComponent } from 'app/shared/components/select/select.component';

@Component({
  selector: 'app-create-organization',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
    TranslateModule,
    PhoneInputDirective,
    SelectComponent,
  ],
  templateUrl: './create-organization.component.html',
  styleUrl: './create-organization.component.scss',
})
export class CreateOrganizationComponent implements OnInit {
  @Input() data: any;
  createForm!: FormGroup;
  isCreating = false;
  timeZones: { name: string; label: string }[] = [];

  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  PhoneNumberFormat = PhoneNumberFormat;
  defaultCountry: CountryISO = CountryISO.Turkey;

  constructor(
    private organizationService: OrganizationService,
    private translate: TranslateService,
    private toastService: ToastService,
    private router: Router,
    private fb: FormBuilder,
    private timezoneService: TimezoneService,
    @Optional() public activeModal?: NgbActiveModal,
  ) {
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);
    this.initCreateForm();
  }

  ngOnInit(): void {
    this.timeZones = this.timezoneService.getAllTimezones();
  }

  initCreateForm(): void {
    this.createForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-ZşŞıİçÇöÖüÜĞğ\s']+$/),
        ],
      ],
      customerCode: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(50)],
      ],
      phone: ['', Validators.required],
      timeZone: [null, [Validators.required, Validators.maxLength(70)]],
      address: ['', [Validators.required, Validators.maxLength(1000)]],
      contactPerson: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-ZşŞıİçÇöÖüÜĞğ\s']+$/),
        ],
      ],
      isAssessmentActive: [false],
    });
  }

  submitCreate(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    this.isCreating = true;
    const raw = this.createForm.getRawValue();
    const phoneInput = raw.phone;

    let finalPhone = '';
    let finalCountryCode = '';

    if (phoneInput && typeof phoneInput === 'object') {
      finalPhone = phoneInput.number
        ? phoneInput.number.replace(/\D/g, '')
        : '';
      finalCountryCode = phoneInput.dialCode;
    } else if (typeof phoneInput === 'string' && phoneInput.trim() !== '') {
      const parsed = parsePhoneNumberFromString(phoneInput);
      if (parsed) {
        finalPhone = parsed.nationalNumber;
        finalCountryCode = '+' + parsed.countryCallingCode;
      } else {
        finalPhone = phoneInput;
      }
    }

    const createRequest = {
      name: raw.name,
      customerCode: raw.customerCode,
      contactPerson: raw.contactPerson,
      email: raw.email,
      address: raw.address,
      timeZone: raw.timeZone,
      phone: finalPhone,
      countryCode: finalCountryCode,
      isAssessmentActive: raw.isAssessmentActive,
    };

    this.organizationService.createOrganization(createRequest).subscribe({
      next: (res: any) => {
        if (res.isSuccess) {
          this.toastService.showToast(
            this.translate.instant('organization.createSuccess'),
            'success',
          );
          if (this.activeModal) {
            this.activeModal.close('success');
          } else {
            this.router.navigate(['/organization/list']);
          }
        } else {
          this.toastService.showToast(
            res.message || 'Creation failed',
            'error',
          );
        }
        this.isCreating = false;
      },
      error: () => {
        this.isCreating = false;
        this.toastService.showToast('Creation failed', 'error');
      },
    });
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.createForm.get(controlName);
    return control
      ? control.invalid && (control.touched || control.dirty)
      : false;
  }

  onCancel(): void {
    if (this.activeModal) {
      this.activeModal.dismiss('cancel');
    } else {
      this.router.navigate(['/organization/list']);
    }
  }
}
